import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import type { Gate1SlideId } from 'contexts/analytics/events/gates';
import { getImpersonateId, getRoles } from 'store/user';
import useGateAnalytics from 'views/Results/Conditions/hooks/useGateAnalytics';
import {
  getGateViewerRole,
  secondsSince,
} from 'views/Results/Conditions/utils/gateAnalytics';

export default function useGate1Tracking(
  active: boolean,
  slideIds: Gate1SlideId[],
) {
  const trackGateEvent = useGateAnalytics('gate_1');
  const { setUserTraits } = useAnalytics();
  const impersonateId = useSelector(getImpersonateId);
  const roles = useSelector(getRoles);

  const viewedAt = useRef(0);
  const slideEnteredAt = useRef(0);
  const backNavigations = useRef(0);

  useEffect(() => {
    if (!active) return;

    viewedAt.current = Date.now();
    slideEnteredAt.current = viewedAt.current;
    backNavigations.current = 0;
    trackGateEvent('Gate 1 Viewed', { slide_count: slideIds.length });
  }, [active]);

  const trackSlideChange = useCallback(
    (from: number, to: number) => {
      if (!active) return;

      const direction = to > from ? 'forward' : 'backward';
      if (direction === 'backward') backNavigations.current += 1;

      trackGateEvent('Gate 1 Slide Advanced', {
        from_slide: slideIds[from],
        to_slide: slideIds[to],
        direction,
        seconds_on_slide: secondsSince(slideEnteredAt.current),
      });
      slideEnteredAt.current = Date.now();
    },
    [active, slideIds, trackGateEvent],
  );

  const trackCompleted = useCallback(() => {
    trackGateEvent('Gate 1 Completed', {
      total_seconds: secondsSince(viewedAt.current),
      back_navigations: backNavigations.current,
    });

    if (getGateViewerRole(impersonateId, roles) === 'user') {
      setUserTraits({ gate_1_completed_at: new Date().toISOString() });
    }
  }, [impersonateId, roles, setUserTraits, trackGateEvent]);

  return { trackSlideChange, trackCompleted };
}
